import type { ClientEngine } from "./engine/client";

/**
 * The recommender API and the zip lookup it depends on, as the organism reads
 * them. Types restate only the fields the organism renders. Once the locale
 * answer names a snapshot, taps are scored in the browser by the same engine.
 */

export interface RecommenderTile {
  id: string;
  label: string;
  perMarket: boolean;
}

/** [id (the call sign), channel number, call sign, name, brand] */
export type RecommenderChannel = [string, string, string, string, string];

export interface RecommenderBootstrap {
  tiles: RecommenderTile[];
  channels: RecommenderChannel[];
}

export interface RecommenderPrice {
  price: number;
  regularPrice: number | null;
  promoDuration: number | null;
  rsnFee: number | null;
}

/** The product item the engine matched to a card or mini, by its position in the request, and its pinned logos. */
export interface RecommenderCardProduct {
  index: number;
  channels: { name: string; logo: string | null }[];
}

export interface RecommenderCard {
  code: string;
  /** Display name from the CMS; absent when it has none for this package. */
  name?: string;
  productClass: string;
  price: RecommenderPrice | null;
  /** A mini on an Epoch promo also carries its regular price and promo length. */
  requiredAddons: { name: string; price: number; mini: boolean; regularPrice?: number; promoDuration?: number; product?: RecommenderCardProduct }[];
  includedContent: { name: string; kind: string; logo?: string | null }[];
  lineup: { count: number; featured: { name: string; logo: string | null }[]; all: string[] };
  /**
   * `addons` groups missing channels by the add-on that would bring them; `core` names, per missed
   * interest, the core channels whose absence alone left it out. Both absent from older servers.
   */
  missing: { interests: string[]; channels: string[]; addons?: { name: string; channels: string[] }[]; core?: { interest: string; channels: string[] }[] };
  combo?: { parts: { code: string; price: number }[]; products?: RecommenderCardProduct[] };
  product?: RecommenderCardProduct;
}

export type AlternativeKind = "cheaper" | "upgrade" | "fallback" | "closest" | null;

/** How the engine chose the cards; `no-full-match` = nothing has every pick. */
export type CardMode = "step-down" | "step-up" | "no-full-match" | "no-alternative-up";

export interface RecommenderMarket {
  zip: string;
  nielsenDma: string;
  county: { name: string; state: string };
  myHomeTeamNetworks: string[];
}

export interface RecommenderAnswer {
  cards: {
    mode: CardMode;
    alternativeKind: AlternativeKind;
    count: 1 | 2;
    top: RecommenderCard;
    alternative: RecommenderCard | null;
  } | null;
  market: RecommenderMarket | null;
  pricesLocalized: boolean;
}

export interface LocaleAnswer {
  market: RecommenderMarket;
  /** The channels a shopper here can buy; replaces the national list in search. */
  channels: RecommenderChannel[];
  /** Present when the browser may score cards itself, naming the snapshot to load. */
  engine?: { version: string };
}

/** What the browser read from the zip lookup, posted to the engine unparsed. */
export interface PostedLocale {
  zip: string;
  county: unknown;
  market: unknown;
}

/** One county the zip lookup returned, enough to name it and look up its market. */
export interface ZipCounty {
  raw: unknown;
  name: string;
  state: string;
  nielsenDma: string;
  /** The site's own DMA code for the county, which its zip module saves beside the Nielsen one. */
  dtvDma: string;
  /** The site's key for the county, its FIPS code then its DMA code, which its zip module saves as `currentCounty`. Empty when the lookup gave no FIPS code or no DMA code. */
  key: string;
}

export const DEFAULT_ZIP = "75001";
const ZIP = /^\d{5}$/;

async function json<T>(response: Response, what: string): Promise<T> {
  if (!response.ok) {
    throw new Error(`${what} responded ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export function isZip(value: unknown): value is string {
  return typeof value === "string" && ZIP.test(value);
}

export function readCookie(cookie: string, name: string): string {
  const found = cookie.split(/;\s*/).find(part => part.startsWith(`${name}=`));
  return found ? found.slice(name.length + 1) : "";
}

/** The zip in the edge's geolocation cookie, or "" when it carries none. */
export function edgescapeZip(cookie: string): string {
  try {
    return decodeURIComponent(readCookie(cookie, "x-dtv-edgescape")).match(/\d{5}/)?.[0] || "";
  } catch {
    return "";
  }
}

/** The cookie the rest of the site keeps the shopper's zip in, then the edge's
 *  geolocation, then the site-wide default — the order Channel Lineup uses. */
export function zipFromCookies(cookie: string): string {
  const current = readCookie(cookie, "currentZipcode");
  if (isZip(current)) {
    return current;
  }
  return edgescapeZip(cookie) || DEFAULT_ZIP;
}

/* The site's own fee cookie format: whole dollars or two decimals. */
const RSN_FEE = /^\d+(\.\d{2})?$/;

/**
 * The regional sports fee the site's cookie holds for this zip, or null. The
 * fee belongs to the zip cookie beside it, so it is trusted only when that zip
 * is this one.
 */
export function rsnFeeFromCookies(cookie: string, zip: string): number | null {
  const fee = readCookie(cookie, "rsnFee");
  return readCookie(cookie, "currentZipcode") === zip && RSN_FEE.test(fee) ? Number(fee) : null;
}

/* One location cookie: site-wide on directv.com, kept 180 days like the site's location cookies. An empty value expires it.
   rsnFee goes through here too, at 180 days, where the site's own fee writer keeps it 182. */
function siteCookie(name: string, value: string, hostname: string): string {
  const domain = /(^|\.)directv\.com$/.test(hostname) ? ";domain=.directv.com" : "";
  const expires = value === "" ? new Date(0) : new Date(Date.now() + 180 * 24 * 60 * 60 * 1000);
  return `${name}=${value}${domain};path=/;expires=${expires.toUTCString()}`;
}

/** The two cookies, as the site's own zip module writes them, so every page reads the same zip and fee. */
export function rsnFeeCookies(zip: string, fee: number, hostname: string): string[] {
  return [siteCookie("currentZipcode", zip, hostname), siteCookie("rsnFee", fee.toFixed(2), hostname)];
}

/**
 * Every location cookie the site's zip module writes when the shopper submits a zip, so a zip applied
 * here is the one every page and new tab opens on. A fee or DMA pair the lookups did not give is expired
 * rather than kept beside a zip it does not belong to; the two DMA codes are only written together.
 */
export function siteLocationCookies(zip: string, county: ZipCounty, fee: number | null, hostname: string): string[] {
  const both = /^\d{1,4}$/.test(county.dtvDma) && /^\d{1,4}$/.test(county.nielsenDma);
  return [
    siteCookie("currentZipcode", zip, hostname),
    siteCookie("currentDma", both ? county.dtvDma : "", hostname),
    siteCookie("currentNielsenDma", both ? county.nielsenDma : "", hostname),
    siteCookie("currentCounty", both ? county.key : "", hostname),
    siteCookie("rsnFee", fee === null ? "" : fee.toFixed(2), hostname)
  ];
}

/* The site's zip-only offers call, the one its zip module makes; the body matches it so the same fee comes back. */
const OFFERS_URL = "/shop/api/getOffers";

/** The fee Epoch charges in this zip: the first tier that has one, else 0. Throws when no tier says. */
export async function fetchRsnFee(zip: string, signal?: AbortSignal): Promise<number> {
  const response = await fetch(OFFERS_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      appName: "UF",
      "idp-source": "DTVS"
    },
    body: JSON.stringify({
      customerEligibility: { zipcode: [zip] },
      pagination: {
        page: 1,
        limit: 100
      },
      offerActionType: ["Acquisition"],
      salesChannel: ["directvOnline"],
      offerProductTypes: ["video-plan"],
      offerProductFamily: ["OTT"],
      addOnType: ["Programming-Bolt-on", "Bolt-on", "Standalone"],
      planSubType: ["cDVR", "Cross-Product-Mobility", "Streams", "CrossProduct-WatchTV", "Cross-product", "International"],
      contractIndicator: ["RR"],
      creditRisk: "Low"
    }),
    signal
  });
  const body = await json<any>(response, "offers");
  const fees: number[] = (Array.isArray(body?.content?.offers) ? body.content.offers : [])
    .map((offer: any) => offer?.attributes?.rsnFeeDetails?.amount)
    /* The site's module takes a numeric string too; matching it keeps both reading the same fee. */
    .filter((amount: unknown) => typeof amount === "number" || (typeof amount === "string" && amount.trim() !== ""))
    .map(Number)
    .filter((amount: number) => Number.isFinite(amount) && amount >= 0);
  if (fees.length === 0) {
    throw new Error("offers answered without a regional sports fee");
  }
  return fees.find(fee => fee > 0) ?? 0;
}

/* Every path ends in "/": the deployed edge 301s a slashless /uf/ path, and a
   browser follows a 301 by turning a POST into a GET. */
export async function fetchBootstrap(apiUrl: string, signal?: AbortSignal): Promise<RecommenderBootstrap> {
  const body = await json<RecommenderBootstrap>(await fetch(`${apiUrl}/bootstrap/`, { signal }), "bootstrap");
  if (!Array.isArray(body?.tiles) || !Array.isArray(body?.channels)) {
    throw new Error("bootstrap answered without tiles or channels");
  }
  return body;
}

/** Counties that carry a market for this zip. Empty when the zip has none. */
export async function lookupCounties(lookupUrl: string, zip: string, signal?: AbortSignal): Promise<ZipCounty[]> {
  const raw = await json<unknown>(await fetch(`${lookupUrl}county/zipcode/${zip}`, { signal }), "county lookup");
  return (Array.isArray(raw) ? raw : [])
    .map((c: any) => ({
      raw: c,
      name: String(c?.countyName || ""),
      state: String(c?.state || ""),
      nielsenDma: String(c?.nielsenDMA || ""),
      dtvDma: String(c?.dtvDMA || ""),
      key: /^\d{5}$/.test(String(c?.fipsCountyCode)) && /^\d{1,4}$/.test(String(c?.dtvDMA)) ? `${c.fipsCountyCode}${c.dtvDMA}` : ""
    }))
    .filter(c => /^\d{1,4}$/.test(c.nielsenDma));
}

export async function lookupMarket(lookupUrl: string, zip: string, nielsenDma: string, signal?: AbortSignal): Promise<unknown> {
  return json<unknown>(await fetch(`${lookupUrl}ottchannels/zipcode/${zip}/${nielsenDma}`, { signal }), "market lookup");
}

function post<T>(url: string, body: unknown, what: string, signal?: AbortSignal): Promise<T> {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal
  }).then(response => json<T>(response, what));
}

/* One engine per page; a newer version named by a later locale answer replaces it. */
let engine: { apiUrl: string; version: string; ready: ClientEngine | null } | null = null;
/* Snapshots that failed to answer a tap; this page never loads them again. A load failure is retried. */
const failed = new Set<string>();
const snapshotKey = (apiUrl: string, version: string) => `${apiUrl} ${version}`;

const whenIdle = (run: () => void): void => {
  // Snyk reads setTimeout fed by fetched data as code injection (CWE-94); the type check and
  // the wrapper are its suggested fix, so setTimeout only ever receives this local function.
  if (typeof run !== "function") {
    return;
  }
  const safeRun = () => {
    run();
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(safeRun, { timeout: 2000 });
  } else {
    setTimeout(safeRun, 0);
  }
};

/** Loads the engine off the tap path, then warms this market's index. A failure leaves taps on the server. */
function startEngine(apiUrl: string, version: string, locale: PostedLocale): void {
  if (failed.has(snapshotKey(apiUrl, version))) {
    return;
  }
  if (engine && engine.apiUrl === apiUrl && engine.version === version) {
    const { ready } = engine;
    if (ready) {
      whenIdle(() => void ready.prewarm(locale));
    }
    return;
  }
  const slot: { apiUrl: string; version: string; ready: ClientEngine | null } = {
    apiUrl,
    version,
    ready: null
  };
  engine = slot;
  whenIdle(() => {
    import("./engine/client")
      .then(async m => {
        const loaded = await m.loadClientEngine(apiUrl, version);
        await loaded.prewarm(locale);
        slot.ready = loaded;
      })
      .catch(() => {
        if (engine === slot) {
          engine = null;
        }
      });
  });
}

/** Test hook. */
export function resetClientEngine(): void {
  engine = null;
  failed.clear();
}

export async function postLocale(apiUrl: string, locale: PostedLocale, signal?: AbortSignal): Promise<LocaleAnswer> {
  const body = await post<LocaleAnswer>(`${apiUrl}/locale/`, { locale }, "locale", signal);
  if (!body?.market || !Array.isArray(body.channels)) {
    throw new Error("locale answered without a market or channels");
  }
  if (typeof body.engine?.version === "string" && typeof window !== "undefined") {
    startEngine(apiUrl, body.engine.version, locale);
  }
  return body;
}

export async function postRecommend(
  apiUrl: string,
  request: {
    tileIds: string[];
    channelIds: string[];
    locale: PostedLocale;
    rsnFee: number | null;
    products: { code: string; channels: string[] }[];
  },
  signal?: AbortSignal
): Promise<RecommenderAnswer> {
  const slot = engine && engine.apiUrl === apiUrl ? engine : null;
  const ready = slot?.ready;
  if (slot && ready) {
    try {
      const answer = await ready.recommend(request) as RecommenderAnswer;
      if (signal?.aborted) {
        throw new DOMException("aborted", "AbortError");
      }
      return answer;
    } catch (err) {
      if (signal?.aborted) {
        throw err;
      }
      // Anything the browser cannot answer, the server still can; this snapshot is not asked again.
      failed.add(snapshotKey(slot.apiUrl, slot.version));
      if (engine === slot) {
        engine = null;
      }
    }
  }
  return post<RecommenderAnswer>(`${apiUrl}/recommend/`, request, "recommend", signal);
}

/**
 * Channels whose name, call sign or brand contains the query, or whose number
 * is the query — the prototype's rule. Two characters minimum.
 */
export function matchChannels(
  channels: RecommenderChannel[],
  query: string,
  limit = 20
): RecommenderChannel[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) {
    return [];
  }
  const out: RecommenderChannel[] = [];
  for (const channel of channels) {
    if (out.length >= limit) {
      break;
    }
    const [, number, callSign, name, brand] = channel;
    if (
      name.toLowerCase().includes(q) ||
      callSign.toLowerCase().includes(q) ||
      brand.toLowerCase().includes(q) ||
      String(Number(number) || number) === q
    ) {
      out.push(channel);
    }
  }
  return out;
}
